// AWS Configuration for My Nerdy Son
// These values will be populated from CDK outputs during deployment

export interface AWSConfig {
  region: string;
  userPoolId: string;
  userPoolWebClientId: string;
  userPoolDomain: string;
  graphqlEndpoint: string;
  s3Bucket: string;
  cloudFrontDomain: string;
}

// Default configuration - will be overridden by environment variables
const defaultConfig: AWSConfig = {
  region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  userPoolId: process.env.REACT_APP_USER_POOL_ID || '',
  userPoolWebClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID || '',
  userPoolDomain: process.env.REACT_APP_USER_POOL_DOMAIN || '',
  graphqlEndpoint: process.env.REACT_APP_GRAPHQL_ENDPOINT || '',
  s3Bucket: process.env.REACT_APP_S3_BUCKET || '',
  cloudFrontDomain: process.env.REACT_APP_CLOUDFRONT_DOMAIN || '',
};

// Validate required configuration
const validateConfig = (config: AWSConfig): void => {
  const requiredFields: (keyof AWSConfig)[] = [
    'region',
    'userPoolId',
    'userPoolWebClientId',
  ];

  const missingFields = requiredFields.filter(field => !config[field]);
  
  if (missingFields.length > 0) {
    console.warn(
      `Missing AWS configuration for: ${missingFields.join(', ')}. ` +
      'Some features may not work properly in development mode.'
    );
  }
};

// Validate configuration on load
validateConfig(defaultConfig);

export const awsConfig = defaultConfig;

// Amplify configuration format (Legacy format for CDK compatibility)
export const amplifyConfig = {
  aws_project_region: awsConfig.region,
  aws_cognito_region: awsConfig.region,
  aws_user_pools_id: awsConfig.userPoolId,
  aws_user_pools_web_client_id: awsConfig.userPoolWebClientId,
  oauth: {
    domain: awsConfig.userPoolDomain,
    scope: ['email', 'openid', 'profile'],
    redirectSignIn: process.env.NODE_ENV === 'production' 
      ? `https://${awsConfig.cloudFrontDomain}/callback`
      : 'http://localhost:3000/callback',
    redirectSignOut: process.env.NODE_ENV === 'production'
      ? `https://${awsConfig.cloudFrontDomain}/logout`
      : 'http://localhost:3000/logout',
    responseType: 'code',
  },
  aws_appsync_graphqlEndpoint: awsConfig.graphqlEndpoint,
  aws_appsync_region: awsConfig.region,
  aws_appsync_authenticationType: 'AMAZON_COGNITO_USER_POOLS',
  aws_user_files_s3_bucket: awsConfig.s3Bucket,
  aws_user_files_s3_bucket_region: awsConfig.region,
};

// Helper function to check if configuration is complete
export const isConfigurationComplete = (): boolean => {
  return !!(
    awsConfig.userPoolId &&
    awsConfig.userPoolWebClientId &&
    awsConfig.region
  );
};

// Helper function to get authentication URLs
export const getAuthUrls = () => {
  const baseUrl = process.env.NODE_ENV === 'production'
    ? `https://${awsConfig.cloudFrontDomain}`
    : 'http://localhost:3000';

  return {
    signIn: `${baseUrl}/signin`,
    signUp: `${baseUrl}/signup`,
    callback: `${baseUrl}/callback`,
    logout: `${baseUrl}/logout`,
  };
};